import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PhoneOff, Mic, MicOff, AlertTriangle, SwitchCamera, Wand2 } from "lucide-react";
import { CoinIcon, RubyIcon } from "@/components/Icons";
import { BEAUTY_DEFAULTS, createBeautyPipeline } from "@/lib/beautyEngine";
import BeautyTray from "@/components/BeautyTray";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useCall } from "@/context/CallContext";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";

const isPermError = (e) => e?.code === "PERMISSION_DENIED" || e?.name === "NotAllowedError" || /permission denied|not allowed/i.test(e?.message || "");
let callJoinLock = Promise.resolve();

export default function CallRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tick, lowBalance, endedSummary, setEndedSummary } = useCall();
  const { user: me } = useAuth();
  const prefs = me?.beauty_prefs;
  const [data, setData] = useState(null);
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [remoteJoined, setRemoteJoined] = useState(false);
  const [params, setParams] = useState(() => ({ ...BEAUTY_DEFAULTS, ...(prefs?.params || {}) }));
  const [presetKey, setPresetKey] = useState(prefs?.preset || "none");
  const [sticker, setSticker] = useState(prefs?.sticker || "none");
  const [makeup, setMakeup] = useState(prefs?.makeup || "none");
  const [mirrored, setMirrored] = useState(prefs?.mirror !== undefined ? prefs.mirror : true);
  const [facing, setFacing] = useState("user");
  const [swapped, setSwapped] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const stateRef = useRef({ params: { ...BEAUTY_DEFAULTS, ...(prefs?.params || {}) }, presetKey: prefs?.preset || "none", sticker: prefs?.sticker || "none", makeup: prefs?.makeup || "none" });
  const swappedRef = useRef(false);
  const bigRef = useRef(null);
  const pipRef = useRef(null);
  const tracksRef = useRef([]);
  const rtcRef = useRef({});

  const pickParams = (p) => {
    stateRef.current.params = p;
    setParams(p);
  };
  const pickPreset = (key) => {
    stateRef.current.presetKey = key;
    setPresetKey(key);
  };
  const pickSticker = (key) => {
    stateRef.current.sticker = key;
    setSticker(key);
  };
  const pickMakeup = (key) => {
    stateRef.current.makeup = key;
    setMakeup(key);
  };

  useEffect(() => {
    const t = setTimeout(() => {
      api.patch("/users/me/beauty-prefs", { params, preset: presetKey, sticker, makeup, mirror: mirrored }).catch((e) => console.warn("Beauty prefs autosave failed", e));
    }, 1200);
    return () => clearTimeout(t);
  }, [params, presetKey, sticker, makeup, mirrored]);

  const layout = () => {
    const p = rtcRef.current;
    const big = bigRef.current, pip = pipRef.current;
    if (!big || !pip) return;
    const sw = swappedRef.current;
    try { p.localVideoTrack && p.localVideoTrack.play(sw ? big : pip); } catch (e) { /* ignore */ }
    try { p.remoteVideoTrack && p.remoteVideoTrack.play(sw ? pip : big); } catch (e) { /* ignore */ }
  };

  const toggleSwap = () => {
    swappedRef.current = !swappedRef.current;
    setSwapped(swappedRef.current);
    layout();
  };

  useEffect(() => {
    api.get(`/calls/${id}`).then(({ data: d }) => {
      if (d.call.status !== "active") {
        if (!endedSummary) toast.error("Call is not active");
        setData(d);
        return;
      }
      setData(d);
    }).catch((e) => {
      toast.error(errText(e));
      navigate(-1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!data?.agora || data.call.status !== "active") return;
    let client;
    let cancelled = false;
    let micTrack, videoTrack, canvasTrack;
    const p = rtcRef.current;
    // Capture synchronously to avoid awaiting our own cleanup chain (deadlock).
    const prevLock = callJoinLock;
    const setup = async () => {
      try {
        const AgoraRTC = (await import("agora-rtc-sdk-ng")).default;
        AgoraRTC.setLogLevel(3);
        p.AgoraRTC = AgoraRTC;

        // Request mic (and camera for video calls) FIRST so the browser
        // permission prompt always appears before any network connection.
        try {
          micTrack = await AgoraRTC.createMicrophoneAudioTrack();
          if (data.call.mode === "video") {
            p.rawCam = await AgoraRTC.createCameraVideoTrack({ facingMode: "user" });
            p.facing = "user";
          }
        } catch (permErr) {
          console.error("Media permission error", permErr);
          [micTrack, p.rawCam].forEach((t) => { try { t && t.stop(); t && t.close(); } catch (e) { /* ignore */ } });
          micTrack = null;
          p.rawCam = null;
          if (isPermError(permErr)) {
            toast.error(data.call.mode === "video"
              ? "Camera & microphone access blocked — allow them in your browser settings (lock icon in the address bar) and rejoin the call."
              : "Microphone access blocked — allow it in your browser settings (lock icon in the address bar) and rejoin the call.");
          } else {
            toast.error("Could not access your camera/microphone");
          }
          return;
        }
        if (cancelled) {
          [micTrack, p.rawCam].forEach((t) => { try { t && t.stop(); t && t.close(); } catch (e) { /* ignore */ } });
          p.rawCam = null;
          return;
        }

        if (data.call.mode === "video") {
          // WebGL pipeline: raw camera -> beauty/filter shader canvas -> published custom track
          const videoEl = document.createElement("video");
          videoEl.muted = true;
          videoEl.playsInline = true;
          videoEl.srcObject = new MediaStream([p.rawCam.getMediaStreamTrack()]);
          await videoEl.play().catch(() => {});
          p.videoEl = videoEl;

          const pipeline = createBeautyPipeline(videoEl, () => stateRef.current);
          p.pipeline = pipeline;
          canvasTrack = pipeline.canvas.captureStream(24).getVideoTracks()[0];
          videoTrack = AgoraRTC.createCustomVideoTrack({ mediaStreamTrack: canvasTrack });
          p.localVideoTrack = videoTrack;
          layout();
        }
        const tracks = videoTrack ? [micTrack, videoTrack] : [micTrack];
        tracksRef.current = tracks;
        if (cancelled) {
          tracks.forEach((t) => { try { t.stop(); t.close(); } catch (e) { /* ignore */ } });
          try { canvasTrack && canvasTrack.stop(); } catch (e) { /* ignore */ }
          [p.rawCam].forEach((t) => { try { t && t.stop(); t && t.close(); } catch (e) { /* ignore */ } });
          p.rawCam = null;
          return;
        }

        client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
        p.client = client;
        client.on("user-published", async (u, mediaType) => {
          await client.subscribe(u, mediaType);
          if (mediaType === "video") {
            p.remoteVideoTrack = u.videoTrack;
            layout();
          }
          if (mediaType === "audio") u.audioTrack.play();
          setRemoteJoined(true);
        });
        client.on("user-unpublished", (u, mediaType) => {
          if (mediaType === "video") p.remoteVideoTrack = null;
        });
        client.on("user-left", () => {
          p.remoteVideoTrack = null;
          setRemoteJoined(false);
        });

        // Wait for any previous client (StrictMode remount) to fully leave
        // before joining with the same UID, preventing UID_CONFLICT.
        await prevLock.catch(() => { /* ignore */ });
        if (cancelled) return;
        console.log("[Yaara Live] CALL CONNECTED! Agora RTC Params:", {
          app_id: data.agora.app_id,
          channel: data.agora.channel,
          token: data.agora.token,
          uid: data.agora.uid,
          ws_signaling_url: `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/ws/user?token=${localStorage.getItem("yaara_token")}`,
          sse_stream_url: `${window.location.protocol}//${window.location.host}/api/ws/user?token=${localStorage.getItem("yaara_token")}&sse=1`
        });
        await client.join(data.agora.app_id, data.agora.channel, data.agora.token, data.agora.uid);
        if (cancelled) {
          await client.leave().catch(() => { /* ignore */ });
          return;
        }
        await client.publish(tracks);
      } catch (e) {
        console.error("Agora call error", e);
        toast.error("Could not connect the call — check your internet connection");
      }
    };
    const setupPromise = setup();
    return () => {
      cancelled = true;
      try { p.pipeline && p.pipeline.stop(); } catch (e) { /* ignore */ }
      callJoinLock = setupPromise
        .catch(() => { /* ignore */ })
        .then(() => (client ? client.leave().catch(() => { /* ignore */ }) : undefined));
      [micTrack, videoTrack, p.rawCam].forEach((t) => {
        try { t && t.stop(); t && t.close(); } catch (e) { /* ignore */ }
      });
      try { canvasTrack && canvasTrack.stop(); } catch (e) { /* ignore */ }
      tracksRef.current = [];
      p.rawCam = null;
      p.videoEl = null;
      p.pipeline = null;
      p.localVideoTrack = null;
      p.remoteVideoTrack = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const toggleMute = () => {
    const audio = tracksRef.current[0];
    if (audio) {
      audio.setEnabled(muted);
      setMuted(!muted);
    }
  };

  const flipCamera = async () => {
    const { AgoraRTC, videoEl, rawCam, facing: cur } = rtcRef.current;
    if (!AgoraRTC || !videoEl) return;
    const next = cur === "environment" ? "user" : "environment";
    let newCam;
    try {
      newCam = await AgoraRTC.createCameraVideoTrack({ facingMode: { exact: next } });
    } catch (e) {
      toast.error(next === "environment" ? "No back camera found on this device" : "Could not switch camera");
      return;
    }
    if (rawCam) {
      rawCam.stop();
      rawCam.close();
    }
    rtcRef.current.rawCam = newCam;
    videoEl.srcObject = new MediaStream([newCam.getMediaStreamTrack()]);
    await videoEl.play().catch(() => {});
    rtcRef.current.facing = next;
    setFacing(next);
  };

  const hangUp = async () => {
    try { await api.post(`/calls/${id}/end`); } catch (e) { console.warn("End-call request failed", e); }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const summary = endedSummary?.call_id === id ? endedSummary : null;

  if (!data) {
    return <div className="min-h-screen max-w-md mx-auto flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-pink-500 border-t-transparent animate-spin" /></div>;
  }

  const isVideo = data.call.mode === "video";
  const spent = tick?.call_id === id ? tick.coins_spent : data.call.coins_spent;
  const earned = tick?.call_id === id ? tick.diamonds_earned : data.call.diamonds_earned;
  const beautyActive = Object.values(params).some((v) => v > 0) || presetKey !== "none" || sticker !== "none" || makeup !== "none";
  const localMirror = mirrored && facing === "user";
  const bigMirror = swapped && localMirror;
  const pipMirror = !swapped && localMirror;
  const showAvatarOverlay = (!remoteJoined && !swapped) || !isVideo;

  return (
    <div className="h-screen-dvh max-w-md mx-auto relative overflow-hidden bg-[#0E0E12]" data-testid="call-room">
      {/* Big screen (remote by default, local when swapped) */}
      <div ref={bigRef} onClick={() => isVideo && remoteJoined === false && swapped ? toggleSwap() : undefined}
        className={`absolute inset-0 [&_video]:object-cover ${bigMirror ? "[&_video]:[transform:scaleX(-1)]" : ""}`}
        data-testid="call-remote-media" />
      {showAvatarOverlay && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
          <div className={remoteJoined ? "" : "pulse-live"}>
            <Avatar src={data.other?.avatar} size={96} tier={data.other?.tier} level={data.other?.level} />
          </div>
          <p className="text-white font-bold font-display">{data.other?.name}</p>
          <p className="text-zinc-500 text-xs">{remoteJoined ? "🎙️ Voice call in progress" : "Connecting…"}</p>
        </div>
      )}

      {/* PiP (local by default, remote when swapped) — tap to swap */}
      {isVideo && (
        <button onClick={toggleSwap} data-testid="local-pip"
          className={`absolute top-16 right-3 w-24 h-36 rounded-2xl overflow-hidden border border-white/20 z-20 bg-black active:scale-95 transition-transform cursor-pointer`}>
          <div ref={pipRef} className={`w-full h-full [&_video]:object-cover ${pipMirror ? "[&_video]:[transform:scaleX(-1)]" : ""}`} />
          {swapped && !remoteJoined && (
            <span className="absolute inset-0 flex items-center justify-center bg-black/60">
              <Avatar src={data.other?.avatar} size={40} />
            </span>
          )}
          <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[8px] font-bold text-white/80 bg-black/50 px-1.5 py-0.5 rounded-full pointer-events-none" data-testid="pip-label">
            {swapped ? data.other?.name?.split(" ")[0] || "Them" : "You"}
          </span>
        </button>
      )}

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 p-3 z-20 flex items-center justify-between">
        <div className="glass-dark rounded-full px-3.5 py-1.5">
          <p className="text-white text-xs font-bold font-display">{data.other?.name}</p>
          <p className="text-zinc-400 text-[9px] flex items-center gap-1">{isVideo ? "Video" : "Voice"} call · <CoinIcon size={10} /> {data.call.rate}/min</p>
        </div>
        <div className="glass-dark rounded-full px-3 py-1.5 text-right">
          <p className="text-white text-xs font-bold" data-testid="call-timer">{fmt(elapsed)}</p>
          <p className="text-[9px] font-semibold" data-testid="call-spend">
            {data.is_caller ? <span className="text-amber-400 flex items-center gap-0.5"><CoinIcon size={10} /> {spent}</span> : <span className="text-rose-300 flex items-center gap-0.5"><RubyIcon size={10} /> {earned}</span>}
          </p>
        </div>
      </div>

      {/* Low balance warning */}
      {lowBalance && !summary && (
        <div className="absolute top-16 left-3 right-3 z-30 glass-dark border-amber-500/60 rounded-2xl px-4 py-2.5 flex items-center gap-2" data-testid="low-balance-warning">
          <AlertTriangle size={16} className="text-amber-400 shrink-0" />
          <p className="text-amber-300 text-xs font-semibold">{data.is_caller ? "Low balance! Call ends in ~30s — recharge coins to continue." : "Caller balance low — call may end in ~30s."}</p>
        </div>
      )}

      {/* Beauty & filters tray */}
      {isVideo && showFilters && !summary && (
        <div className="absolute bottom-32 left-0 right-0 z-30 px-3" data-testid="call-filters-tray">
          <BeautyTray
            params={params} onParams={pickParams}
            presetKey={presetKey} onPreset={pickPreset}
            sticker={sticker} onSticker={pickSticker}
            makeup={makeup} onMakeup={pickMakeup}
            mirrored={mirrored} onMirror={() => setMirrored((m) => !m)}
            testPrefix="call-"
          />
        </div>
      )}

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-4 pb-10 z-20 flex items-center justify-center gap-5">
        <button data-testid="mute-button" onClick={toggleMute}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${muted ? "bg-white text-black" : "glass-dark text-white"}`}>
          {muted ? <MicOff size={18} /> : <Mic size={18} />}
        </button>
        {isVideo && (
          <button data-testid="call-flip-camera-button" onClick={flipCamera}
            className="w-12 h-12 rounded-full glass-dark text-white flex items-center justify-center active:scale-90 transition-transform">
            <SwitchCamera size={18} />
          </button>
        )}
        {isVideo && (
          <button data-testid="call-filters-button" onClick={() => setShowFilters((s) => !s)}
            className={`w-12 h-12 rounded-full glass-dark flex items-center justify-center active:scale-90 transition-transform ${showFilters || beautyActive ? "text-pink-400" : "text-white"}`}>
            <Wand2 size={18} />
          </button>
        )}
        <button data-testid="end-call-button" onClick={hangUp}
          className="w-16 h-16 rounded-full bg-rose-600 flex items-center justify-center text-white shadow-[0_0_25px_rgba(244,63,94,0.6)] active:scale-90 transition-transform">
          <PhoneOff size={24} />
        </button>
      </div>

      {/* Summary overlay */}
      {(summary || (data.call.status !== "active" && !summary)) && (
        <div className="absolute inset-0 z-50 bg-[#0B0F19]/95 backdrop-blur-md flex flex-col items-center justify-center px-8 text-center" data-testid="call-summary-overlay">
          <Avatar src={data.other?.avatar} size={72} tier={data.other?.tier} />
          <h2 className="text-white font-extrabold text-xl mt-4 font-display">Call Ended</h2>
          {summary?.reason === "insufficient_balance" && <p className="text-amber-400 text-xs mt-1">Ended — coin balance ran out</p>}
          <div className="grid grid-cols-2 gap-3 mt-6 w-full">
            <div className="card-luxe rounded-2xl p-3"><p className="text-2xl font-extrabold gradient-text">{summary?.minutes ?? data.call.minutes_billed}m</p><p className="text-[10px] text-zinc-400">Billed minutes</p></div>
            <div className="card-luxe rounded-2xl p-3">
              {data.is_caller
                ? <><p className="text-2xl font-extrabold text-amber-400 flex items-center justify-center gap-1.5"><CoinIcon size={20} /> {(summary?.coins_spent ?? data.call.coins_spent).toLocaleString()}</p><p className="text-[10px] text-zinc-400">Coins spent</p></>
                : <><p className="text-2xl font-extrabold text-rose-300 flex items-center justify-center gap-1.5"><RubyIcon size={20} /> {(summary?.diamonds_earned ?? data.call.diamonds_earned).toLocaleString()}</p><p className="text-[10px] text-zinc-400">Rubies earned</p></>}
            </div>
          </div>
          <button data-testid="call-back-home" onClick={() => { setEndedSummary(null); navigate("/"); }}
            className="mt-8 gradient-primary text-white font-bold px-8 py-3 rounded-full active:scale-95 transition-transform">
            Done
          </button>
        </div>
      )}
    </div>
  );
}
